import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AtoZDropdown, { atoZLetters } from "./AtoZDropdown";
import UserMenu from "./UserMenu";

const LOGO_URL =
  "https://storage.helloreaddy.io/project_files/9a028e4c-ff87-4d3e-a810-ef1048f4ad5f/d01b20cb-70c5-407a-bc10-7f897ccd03bd_compressed_coastal-logo-Photoroom.webp";

const primaryNav = [
  { label: "Home", to: "/" },
  { label: "Featured", to: "/featured-listings" },
  { label: "All Listings", to: "/directory" },
  { label: "Location Wise", to: "/locations" },
  { label: "Essentials", to: "/categories" },
  { label: "Tourist Spots", to: "/tourist-spots" },
];

const moreNav = [
  { label: "Why Us", to: "/why-us" },
  { label: "Listing Plans", to: "/listing-plans" },
  { label: "FAQs", to: "/faqs" },
];

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [mobileCategoriesOpen, setMobileCategoriesOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setMoreOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(event.target as Node)) {
        setMoreOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleLogout() {
    setMenuOpen(false);
    logout();
    navigate("/login");
  }

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `relative whitespace-nowrap px-1 py-2 text-sm font-medium transition-colors duration-200 ${
      isActive ? "text-primary-700" : "text-foreground-700 hover:text-primary-700"
    }`;

  const moreActive = moreNav.some((item) => location.pathname.startsWith(item.to));

  return (
    <>
      <div className="hidden bg-primary-900 text-background-100 md:block">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 py-2 text-xs">
          <p className="flex items-center gap-2">
            <span className="flex h-3.5 w-3.5 items-center justify-center text-accent-400">
              <i className="ri-map-pin-2-fill text-[13px]" />
            </span>
            Your Trusted Business Directory — South Canara • Udupi • North Canara
          </p>
          <div className="flex items-center gap-5">
            <Link
              to="/emergency-services"
              className="inline-flex items-center gap-1.5 whitespace-nowrap font-semibold text-red-300 transition-colors hover:text-red-200"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-alarm-warning-line text-[13px]" />
              </span>
              24×7 Emergency
            </Link>
            <span className="text-background-300">GSTIN: 29KIZPK4767D1Z9</span>
            <a
              href="mailto:info@coastalbizzconnect.com"
              className="inline-flex items-center gap-1.5 text-background-100 transition-colors hover:text-accent-300"
            >
              <span className="flex h-3.5 w-3.5 items-center justify-center">
                <i className="ri-mail-line text-[13px]" />
              </span>
              info@coastalbizzconnect.com
            </a>
          </div>
        </div>
      </div>

      <header className="sticky top-0 z-50 border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-6 px-6 py-3">
          <Link to="/" className="flex shrink-0 items-center" aria-label="Coastal Bizz Connect home">
            <img
              src={LOGO_URL}
              alt="Coastal Bizz Connect — Your Trusted Business Directory"
              title="Coastal Bizz Connect"
              className="h-12 w-auto md:h-14"
            />
          </Link>

          <nav className="hidden items-center gap-x-5 xl:flex">
            {primaryNav.map((item) => (
              <NavLink key={item.to} to={item.to} className={linkClass} end={item.to === "/"}>
                {item.label}
              </NavLink>
            ))}

            <AtoZDropdown />

            <div className="relative" ref={moreRef}>
              <button
                type="button"
                onClick={() => setMoreOpen((open) => !open)}
                aria-haspopup="true"
                aria-expanded={moreOpen}
                className={`inline-flex cursor-pointer items-center gap-1 whitespace-nowrap px-1 py-2 text-sm font-medium transition-colors duration-200 ${
                  moreActive || moreOpen
                    ? "text-primary-700"
                    : "text-foreground-700 hover:text-primary-700"
                }`}
              >
                More
                <span className="flex h-4 w-4 items-center justify-center">
                  <i
                    className={
                      moreOpen ? "ri-arrow-up-s-line text-base" : "ri-arrow-down-s-line text-base"
                    }
                  />
                </span>
              </button>
              {moreOpen ? (
                <div className="absolute right-0 top-full z-50 mt-2 w-52 overflow-hidden rounded-md border border-background-200 bg-background-50 py-1.5">
                  {moreNav.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setMoreOpen(false)}
                      className={({ isActive }) =>
                        `block px-4 py-2 text-sm font-medium transition-colors ${
                          isActive
                            ? "bg-primary-50 text-primary-700"
                            : "text-foreground-700 hover:bg-background-100"
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              ) : null}
            </div>
          </nav>

          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <UserMenu />
            ) : (
              <Link
                to="/login"
                className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-[#CFF1E6] px-4 py-2.5 text-sm font-semibold text-foreground-950 transition-colors duration-200 hover:bg-[#bde6d8]"
              >
                <span className="flex h-3.5 w-3.5 items-center justify-center">
                  <i className="ri-user-line text-[13px]" />
                </span>
                Members Login
              </Link>
            )}
            <Link
              to="/list-your-business"
              className="hidden whitespace-nowrap rounded-md bg-[#A3C7EB] px-4 py-2.5 text-sm font-semibold text-foreground-950 transition-colors duration-200 hover:bg-[#8db8e3] md:inline-flex"
            >
              Add your Listing
            </Link>
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-label="Toggle navigation menu"
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 transition-colors hover:bg-background-100 xl:hidden"
            >
              <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
            </button>
          </div>
        </div>

        {menuOpen ? (
          <div className="border-t border-background-200 bg-background-50 xl:hidden">
            <nav className="mx-auto flex max-w-[1400px] flex-col px-6 py-3">
              {primaryNav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/"}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `border-b border-background-100 py-3 text-sm font-medium ${
                      isActive ? "text-primary-700" : "text-foreground-700"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}

              <button
                type="button"
                onClick={() => setMobileCategoriesOpen((open) => !open)}
                aria-expanded={mobileCategoriesOpen}
                className="flex w-full cursor-pointer items-center justify-between border-b border-background-100 py-3 text-left text-sm font-medium text-foreground-700"
              >
                A to Z Categories
                <span className="flex h-4 w-4 items-center justify-center text-foreground-500">
                  <i
                    className={
                      mobileCategoriesOpen
                        ? "ri-arrow-up-s-line text-base"
                        : "ri-arrow-down-s-line text-base"
                    }
                  />
                </span>
              </button>
              {mobileCategoriesOpen ? (
                <div className="grid grid-cols-7 gap-1.5 border-b border-background-100 py-3">
                  {atoZLetters.map((letter) => (
                    <Link
                      key={letter}
                      to={`/categories-a-z#letter-${letter}`}
                      onClick={() => setMenuOpen(false)}
                      aria-label={`Categories starting with ${letter}`}
                      className="flex h-8 items-center justify-center rounded-md bg-background-100 text-xs font-bold uppercase text-foreground-700 transition-colors hover:bg-secondary-100 hover:text-secondary-900"
                    >
                      {letter}
                    </Link>
                  ))}
                  <Link
                    to="/categories-a-z"
                    onClick={() => setMenuOpen(false)}
                    className="col-span-7 mt-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-primary-500 px-3 py-2 text-xs font-semibold text-background-50"
                  >
                    Browse the full A to Z index
                    <span className="flex h-3.5 w-3.5 items-center justify-center">
                      <i className="ri-arrow-right-line text-sm" />
                    </span>
                  </Link>
                </div>
              ) : null}

              <p className="pb-1 pt-4 text-[11px] font-bold uppercase tracking-wider text-foreground-500">
                More
              </p>
              {moreNav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `border-b border-background-100 py-3 text-sm font-medium ${
                      isActive ? "text-primary-700" : "text-foreground-700"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}

              <div className="my-3 border-t border-background-100" />
              {isAuthenticated ? (
                <>
                  <NavLink
                    to="/account"
                    end
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 border-b border-background-100 py-3 text-sm font-medium ${
                        isActive ? "text-primary-700" : "text-foreground-700"
                      }`
                    }
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-dashboard-line text-base" />
                    </span>
                    Dashboard
                  </NavLink>
                  <NavLink
                    to="/listings"
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 border-b border-background-100 py-3 text-sm font-medium ${
                        isActive ? "text-primary-700" : "text-foreground-700"
                      }`
                    }
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-store-3-line text-base" />
                    </span>
                    My Listings
                  </NavLink>
                  <NavLink
                    to="/profile"
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 border-b border-background-100 py-3 text-sm font-medium ${
                        isActive ? "text-primary-700" : "text-foreground-700"
                      }`
                    }
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-user-settings-line text-base" />
                    </span>
                    Business Profile
                  </NavLink>
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="flex cursor-pointer items-center gap-2.5 border-b border-background-100 py-3 text-left text-sm font-medium text-foreground-700"
                  >
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-logout-box-r-line text-base" />
                    </span>
                    Sign out
                  </button>
                </>
              ) : (
                <NavLink
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2.5 border-b border-background-100 py-3 text-sm font-medium text-foreground-700"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-user-line text-base" />
                  </span>
                  Members Login
                </NavLink>
              )}
              <Link
                to="/list-your-business"
                onClick={() => setMenuOpen(false)}
                className="mt-4 inline-flex items-center justify-center whitespace-nowrap rounded-md bg-[#A3C7EB] px-4 py-3 text-sm font-semibold text-foreground-950"
              >
                Add your Listing
              </Link>
            </nav>
          </div>
        ) : null}
      </header>
    </>
  );
}